/**
 * The GM password prompt in front of a GM-only section (src/persistence/gm-lock.ts).
 * Locked: a password field. Unlocked: the section, with a "Lock" button.
 */
import { useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { lockGm, tryUnlockGm, useGmUnlocked } from '@/persistence/gm-lock';
import { useAppState } from '@/state/store';
import '@/ui/gm-lock.css';

const WORDS = {
  title: { tr: 'GM şifresi gerekli', en: 'GM password required' },
  hint: {
    tr: 'Bu bölüm GM içindir: hikâyeyi ve kampanya paketlerini oyunculardan saklar.',
    en: 'This part is for the GM: it keeps the story and the campaign packs away from the players.',
  },
  field: { tr: 'Şifre', en: 'Password' },
  open: { tr: 'Aç', en: 'Unlock' },
  wrong: { tr: 'Şifre yanlış. Tekrar deneyin.', en: 'Wrong password. Try again.' },
  lock: { tr: 'Kilitle', en: 'Lock' },
  unlocked: { tr: 'GM kilidi açık', en: 'GM lock open' },
};

export default function GmLockGate({ id, label, children }: { id: string; label: string; children: ReactNode }): ReactElement {
  const unlocked = useGmUnlocked();
  const { ui } = useAppState();
  const say = (value: { tr: string; en: string }): string => value[ui.locale];
  const [password, setPassword] = useState('');
  const [wrong, setWrong] = useState(false);
  const [busy, setBusy] = useState(false);

  if (unlocked) {
    return (
      <>
        <div className="gm-lock-bar" data-gm-lock="open">
          <span className="small muted">🔓 {say(WORDS.unlocked)}</span>
          <button type="button" className="btn mini secondary" onClick={() => lockGm()}>
            {say(WORDS.lock)}
          </button>
        </div>
        {children}
      </>
    );
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    const ok = await tryUnlockGm(password);
    setBusy(false);
    setWrong(!ok);
    if (ok) setPassword('');
  };

  return (
    <section className="card gm-lock" data-gm-lock="closed">
      <h2>
        🔒 {label} — {say(WORDS.title)}
      </h2>
      <p className="muted">{say(WORDS.hint)}</p>
      <form className="gm-lock-form" onSubmit={(event) => void submit(event)}>
        <label className="field" htmlFor={`gm-lock-${id}`}>
          {say(WORDS.field)}
        </label>
        <div className="field-row">
          <input
            id={`gm-lock-${id}`}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setWrong(false);
            }}
          />
          <button type="submit" className="btn" disabled={busy || password === ''}>
            {say(WORDS.open)}
          </button>
        </div>
        {wrong ? (
          <p className="small warn" role="alert">
            {say(WORDS.wrong)}
          </p>
        ) : null}
      </form>
    </section>
  );
}
